import { ConnectDragSource } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ActionHeader from '@/components/organisms/Dialogs/ActionHeader/ActionHeader';
import DialogTooltip from '@/components/organisms/Dialogs/DialogTooltip/DialogTooltip';
import { cn } from '@/lib/utils';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import { selectIsIdInFlow, selectIsNodeConditionRequisiteSelected } from '@/redux/nodes/selectors';
import { Condition } from '@/shared/types/node';
import { renderTextWithVariables } from '@/util/markdown';
import { capitalizeFirstLetter } from '@/util/util';

import { BoxErrorIcon } from '../../../Action/Action/Action';

import styles from './ConditionBox.module.scss';

interface ConditionBoxProps {
  dragRef: ConnectDragSource;
  isSelected: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  match: string;
  name: string;
  id: string;
  index: number;
  isCollapsed: boolean;
  onDelete: () => void;
  condition: Condition;
}

const ConditionBox = ({
  dragRef,
  isSelected,
  onClick,
  onKeyDown,
  name,
  id,
  index,
  isCollapsed,
  onDelete,
  condition,
}: ConditionBoxProps) => {
  const { t } = useTranslation();
  const isActionInFlow = useSelector(selectIsIdInFlow(id));
  const isAnythingSelected = useSelector(selectIsNodeConditionRequisiteSelected);
  const hasDialogError = useSelector(selectHasError(id));
  const guideline = condition.rules?.find((r) => r.operator === 'guideline');

  const isGuideline = condition.match === 'all' && guideline;

  const conditionMatchLabel = isGuideline
    ? t('dialog.condition.guideline')
    : t(`dialog.condition.match_${condition.match}`);

  return (
    <div
      key={id}
      ref={(node) => {
        dragRef(node);
      }}
      className={cn(styles.condition, {
        [styles['condition--selected']]: isSelected && !hasDialogError,
        [styles['condition--selectedError']]: isSelected && hasDialogError,
        [styles.collapsed]: isCollapsed,
        [styles.opacity]: !isActionInFlow && !isAnythingSelected,
      })}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={index}
    >
      {isCollapsed ? (
        <DialogTooltip onDelete={onDelete} type="condition">
          <div className={cn('text-sm leading-5 font-semibold', styles.collapsed__content)}>
            {index + 1}
          </div>
        </DialogTooltip>
      ) : (
        <section className={cn(styles.box, 'relative')}>
          {hasDialogError && <BoxErrorIcon />}

          <ActionHeader
            onDelete={onDelete}
            type="condition"
            objectToCopy={condition}
            isSelected={isSelected}
            hasDialogError={hasDialogError}
          />
          <div className={styles.condition__content}>
            <p className="text-sm leading-5 font-normal text-foreground-muted">{name}</p>
            {isGuideline && guideline?.value && (
              <p className="text-sm leading-5 font-normal text-foreground-muted">
                {renderTextWithVariables(guideline.value)}
              </p>
            )}

            <div className={styles.condition__rules}>
              <span className="inline-flex h-6 items-center rounded-full bg-primary px-3 text-xs text-primary-foreground">
                {capitalizeFirstLetter(conditionMatchLabel)}
              </span>

              {condition.match !== 'else' && !isGuideline && (
                <span className="text-sm leading-5 font-normal text-foreground-muted">
                  {condition.rules?.length === 1
                    ? t('dialog.condition.rule.single')
                    : t('dialog.condition.rule.many', { 0: condition.rules?.length })}
                </span>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ConditionBox;
